import React, { useState } from "react";

const SERVICES = [
  {
    key: "residential",
    label: "Residential",
    img: `${process.env.PUBLIC_URL}/images/residential.jpg`,
    description:
      "studio Brehaut works closely with our clients to design homes that fulfill their needs and desires. We look at site layout, neighbouring properties, sun direction, and view lines. We source materials for a sustainable design, work closely with contractors to oversee every phase of construction, and are available to coordinate with interior designers and landscape architects.",
    pull: "We don't simply design houses. We envision the whole home.",
    deliverables: [
      "Site analysis & feasibility",
      "Schematic design",
      "Construction documentation",
      "Permit coordination",
    ],
  },
  {
    key: "commercial",
    label: "Commercial",
    img: `${process.env.PUBLIC_URL}/images/commercial.jpg`,
    description:
      "studio Brehaut applies advanced skills and software to provide modern, sophisticated designs. We work with a highly experienced team of consultants that encompasses every aspect of construction — including mechanical, electrical, and structural engineers. We approach every project with an eye to the future to create well-thought-out spaces to fit your company.",
    pull: null,
    deliverables: [
      "Site analysis & zoning review",
      "Schematic design",
      "Construction documentation",
      "Permit coordination",
    ],
  },
  {
    key: "institutional",
    label: "Institutional",
    img: `${process.env.PUBLIC_URL}/images/institutional.jpg`,
    description:
      "studio Brehaut has worked on a variety of institutional projects including long-term care facilities, community centres, churches, and schools. Our knowledgeable team designs bright, comfortable, modern spaces that accommodate barrier-free requirements.",
    pull: null,
    deliverables: [
      "Site analysis & master planning",
      "Schematic design",
      "Construction documentation",
      "Permit coordination",
    ],
  },
];

export const Services = () => {
  const [active, setActive] = useState("residential");
  const current = SERVICES.find((s) => s.key === active);

  const goToContact = (e) => {
    e.preventDefault();
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-28 md:py-40 relative"
    >
      <div
        aria-hidden
        className="absolute left-0 right-0 top-0 h-[1px]"
        style={{ backgroundColor: "rgba(200,184,154,0.22)" }}
      />
      <div className="container-editorial">
        <div className="reveal mb-12 md:mb-20 max-w-3xl">
          <p
            className="text-[11px] uppercase font-body mb-6"
            style={{ color: "#3a7d7b", letterSpacing: "0.36em" }}
          >
            — Services
          </p>
          <h2
            className="font-display leading-[1.02] tracking-[-0.018em]"
            style={{ fontSize: "clamp(2.25rem, 5vw, 4.75rem)", fontWeight: 400 }}
          >
            Three practices, <em className="italic">one temperament</em>.
          </h2>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Services"
          className="flex items-end gap-8 md:gap-14 border-b mb-12 md:mb-20 overflow-x-auto"
          style={{ borderColor: "hsl(var(--foreground) / 0.15)" }}
          data-testid="services-tablist"
        >
          {SERVICES.map((s) => {
            const isActive = active === s.key;
            return (
              <button
                key={s.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(s.key)}
                data-testid={`services-tab-${s.key}`}
                className={`relative pb-5 pt-2 font-display transition-colors whitespace-nowrap ${
                  isActive ? "text-foreground" : "text-foreground/40 hover:text-foreground/75"
                }`}
                style={{ fontSize: "clamp(1.4rem, 2.4vw, 2.125rem)", fontWeight: 400 }}
              >
                {s.label}
                <span
                  aria-hidden
                  className="absolute left-0 right-0 -bottom-[1px] h-[2px] transition-all duration-500"
                  style={{
                    backgroundColor: "#3a7d7b",
                    transformOrigin: "left",
                    transform: isActive ? "scaleX(1)" : "scaleX(0)",
                  }}
                />
              </button>
            );
          })}
        </div>

        <div
          key={current.key}
          className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start fade-up-1"
          data-testid={`services-panel-${current.key}`}
        >
          <div className="md:col-span-7">
            <div className="relative overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
              <img
                src={current.img}
                alt={`${current.label} architecture by studio Brehaut`}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          <div className="md:col-span-5">
            <p
              className="font-body text-base md:text-[17px] leading-[1.85]"
              style={{ color: "hsl(var(--foreground) / 0.85)" }}
            >
              {current.description}
            </p>

            {current.pull && (
              <p
                className="mt-8 font-display italic leading-[1.2]"
                style={{ color: "#3a7d7b", fontSize: "clamp(1.35rem, 2vw, 1.75rem)" }}
              >
                “{current.pull}”
              </p>
            )}

            <ul className="mt-10 md:mt-12 space-y-3.5">
              {current.deliverables.map((d) => (
                <li
                  key={d}
                  className="flex items-start gap-4 font-body text-sm md:text-[15px]"
                  style={{ color: "hsl(var(--foreground) / 0.82)" }}
                >
                  <span
                    aria-hidden
                    className="mt-[0.7rem] h-[1px] w-7 shrink-0"
                    style={{ backgroundColor: "#3a7d7b" }}
                  />
                  {d}
                </li>
              ))}
            </ul>

            <a
              href="#contact"
              onClick={goToContact}
              data-testid={`services-cta-${current.key}`}
              className="mt-12 inline-flex items-center gap-3 font-body text-[12px] tracking-[0.22em] uppercase pb-2 border-b transition-all hover:gap-5"
              style={{ color: "#3a7d7b", borderColor: "#3a7d7b" }}
            >
              → Discuss your {current.label} project
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
